import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  Slide,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { type SetStateAction, useEffect, useMemo, useState } from "react";
import { MutateCellForm } from "@dmca/frontend/widgets/admin";
import { CellFeaturesEnum } from "@dmca/models/interface/cell-location";
import AddIcon from "@mui/icons-material/Add";
import {
  AddIOTDeviceModal,
  IOTAdminWidget,
} from "@dmca/frontend/widgets/admin";
import { lazyWithPreload } from "@dmca/frontend/hooks";
const IOTDeviceDetailPanel = lazyWithPreload(() =>
  import("@dmca/frontend/widgets/admin").then((mod) => ({
    default: mod.IOTDeviceDetailPanel,
  }))
);
import { Input } from "@dmca/frontend/components/input";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Button } from "@dmca/frontend/components/button";
import { ConfirmationModal } from "@dmca/frontend/components/modal";
import { toast } from "react-toastify";
import { object, optional, string } from "valibot";

export const Route = createFileRoute("/_authed/_drawerLayout/admin/cell/$id")({
  component: RouteComponent,
  validateSearch: object({
    iot: optional(string()),
  }),
  loader: async ({ context: { trpcQcProxy: trpc, qc }, params: { id } }) => {
    const cells = await qc.ensureQueryData(trpc.cell.getAll.queryOptions());
    const cell = cells.find((x) => x.cellId === id);
    if (!cell) {
      throw notFound();
    }
    qc.prefetchQuery(trpc.plant.getAll.queryOptions());
    qc.prefetchQuery(
      trpc.iot.getAllWithStatus.queryOptions({
        byLocationIds: [cell.cellId],
      })
    );
    qc.prefetchQuery(
      trpc.machineSensor.getMultiple.queryOptions({
        byLocationIds: [cell.cellId],
      })
    );
    qc.prefetchQuery(
      trpc.pulseMeter.getMultiple.queryOptions({
        byLocationIds: [cell.cellId],
      })
    );
  },
});

function RouteComponent() {
  const { trpcQcProxy: trpc, qc } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const id = Route.useParams({
    select: (params) => params.id,
  });
  const initialSelectedIOTDeviceId = Route.useSearch({
    select: (search) => search.iot,
  });

  const { data: cell } = useSuspenseQuery(
    trpc.cell.getAll.queryOptions(undefined, {
      select: (data) => {
        const resp = data.find((x) => x.cellId === id);
        if (!resp) {
          throw notFound();
        }
        return resp;
      },
    })
  );

  const { data: iotDevices } = useSuspenseQuery(
    trpc.iot.getAllWithStatus.queryOptions({
      byLocationIds: [cell.cellId],
    })
  );

  const { data: machineSensors } = useSuspenseQuery(
    trpc.machineSensor.getMultiple.queryOptions({
      byLocationIds: [cell.cellId],
    })
  );

  const { data: pulseMeters } = useSuspenseQuery(
    trpc.pulseMeter.getMultiple.queryOptions({
      byLocationIds: [cell.cellId],
    })
  );

  const parsedIotDevices = useMemo(() => {
    return iotDevices.map((iotDevice) => {
      const linkedMachineSensor = machineSensors.find(
        (x) => x.iotDeviceId === iotDevice.id
      );
      return {
        iotDevice,
        machineSensor: linkedMachineSensor,
      };
    });
  }, [iotDevices, machineSensors]);

  const deleteMutation = useMutation(
    trpc.cell.deleteCell.mutationOptions({
      onSuccess: () => {
        toast.success("Cell deleted successfully.");
        qc.invalidateQueries(trpc.cell.getAll.queryOptions());
        navigate({
          to: "/admin/cell",
          replace: true,
        });
      },
    })
  );

  const jobNotificationToggleMutation = useMutation(
    trpc.cell.update.mutationOptions({
      onSuccess: () => {
        qc.invalidateQueries(trpc.cell.getAll.queryFilter());
      },
      onError: () => {
        toast.error("Failed to update job notification setting.");
      },
    })
  );

  const onDeleteCellConfirm = () => {
    deleteMutation.mutate({
      ids: [cell.cellId],
    });
    setShowDeleteCellModal(false);
  };

  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [showDeleteCellModal, setShowDeleteCellModal] = useState(false);

  // open and selectedIOTDeviceId needs to be separate to avoid re-mount issues
  const [showIOTDevicePanel, innerSetShowIOTDevicePanel] = useState({
    open: !!initialSelectedIOTDeviceId,
    selectedIOTDeviceId: initialSelectedIOTDeviceId || "",
  });

  const setShowIOTDevicePanel = (
    value: SetStateAction<{ open: boolean; selectedIOTDeviceId: string }>
  ) => {
    const newValue =
      typeof value === "function" ? value(showIOTDevicePanel) : value;
    const selectedIOTDeviceIdIsDifferent =
      newValue.selectedIOTDeviceId !== showIOTDevicePanel.selectedIOTDeviceId;
    innerSetShowIOTDevicePanel(newValue);
    if (selectedIOTDeviceIdIsDifferent) {
      navigate({
        search: newValue.selectedIOTDeviceId
          ? { iot: newValue.selectedIOTDeviceId }
          : {},
        replace: true,
      });
    }
  };

  const selectedDevice = useMemo(() => {
    return iotDevices.find(
      (x) => x.id === showIOTDevicePanel.selectedIOTDeviceId
    );
  }, [iotDevices, showIOTDevicePanel.selectedIOTDeviceId]);

  useEffect(() => {
    innerSetShowIOTDevicePanel({
      open: !!initialSelectedIOTDeviceId,
      selectedIOTDeviceId: initialSelectedIOTDeviceId || "",
    });
  }, [id, initialSelectedIOTDeviceId]);

  useEffect(() => {
    if (showIOTDevicePanel.selectedIOTDeviceId && !selectedDevice) {
      innerSetShowIOTDevicePanel({
        open: false,
        selectedIOTDeviceId: "",
      });
      navigate({
        search: {},
        replace: true,
      });
    }
  }, [navigate, selectedDevice, showIOTDevicePanel.selectedIOTDeviceId]);

  const selectedPulseMeters = useMemo(() => {
    return pulseMeters.filter(
      (x) => x.iotDeviceId === showIOTDevicePanel.selectedIOTDeviceId
    );
  }, [pulseMeters, showIOTDevicePanel.selectedIOTDeviceId]);

  const selectedMachineSensors = useMemo(() => {
    return machineSensors.filter(
      (x) => x.iotDeviceId === showIOTDevicePanel.selectedIOTDeviceId
    );
  }, [machineSensors, showIOTDevicePanel.selectedIOTDeviceId]);

  return (
    <>
      <Box
        sx={{
          // All for IOTDevicePanel to overlay on top
          display: "grid",
          gridTemplateColumns: "1fr",
          gridTemplateRows: "auto",
          gap: 0,
          width: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
          "& > *": {
            gridArea: "1/1/2/2",
          },
        }}
      >
        <Box sx={{ px: 2 }}>
          <Input value={cell.cellId} label="ID" disabled />
          <MutateCellForm cell={cell} type="update" />
          <Divider />
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="h6">IoT Devices</Typography>
            <IconButton
              edge="end"
              size="small"
              onClick={() => setShowAddDeviceModal(true)}
            >
              <AddIcon />
            </IconButton>
          </Stack>
          <Stack
            direction="row"
            useFlexGap
            sx={{
              gap: 1,
              flexWrap: "wrap",
              justifyContent: "space-evenly",
              "& > *": { flex: "0 1 200px" },
            }}
          >
            {parsedIotDevices.map((x) => (
              <IOTAdminWidget
                iotDevice={x.iotDevice}
                key={x.iotDevice.id}
                onMouseEnter={() => {
                  IOTDeviceDetailPanel.preload();
                }}
                onClick={() =>
                  setShowIOTDevicePanel({
                    open: true,
                    selectedIOTDeviceId: x.iotDevice.id,
                  })
                }
              />
            ))}
          </Stack>
          <AddIOTDeviceModal
            open={showAddDeviceModal}
            cell={cell}
            onClose={() => setShowAddDeviceModal(false)}
          />
          <Divider sx={{ my: 2 }} />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography variant="h6">Job Notifications</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={cell.featureFlag.includes(
                    CellFeaturesEnum.JOB_NOTIFICATION
                  )}
                  disabled={jobNotificationToggleMutation.isPending}
                  onChange={(e) => {
                    const current = cell.featureFlag;
                    const updated = e.target.checked
                      ? [...current, CellFeaturesEnum.JOB_NOTIFICATION]
                      : current.filter(
                          (f) => f !== CellFeaturesEnum.JOB_NOTIFICATION
                        );
                    jobNotificationToggleMutation.mutate({
                      id: cell.cellId,
                      featureFlag: updated,
                    });
                  }}
                />
              }
              label="Enable"
              labelPlacement="start"
            />
          </Stack>
          {cell.featureFlag.includes(CellFeaturesEnum.JOB_NOTIFICATION) && (
            <Typography variant="body2" color="text.secondary">
              Job notifications are enabled. Configure notification contacts in{" "}
              <strong>
                Admin &rsaquo; Manage Notifications &rsaquo; Job Notification
              </strong>
              .
            </Typography>
          )}
          <Accordion
            sx={{
              mt: 2,
              "&.Mui-expanded:first-of-type": {
                mt: 2,
              },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography component="span">Advanced</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Button
                fullWidth
                color="error"
                onClick={() => setShowDeleteCellModal(true)}
                loading={deleteMutation.isPending}
              >
                Remove Cell
              </Button>
            </AccordionDetails>
          </Accordion>
          {showDeleteCellModal && (
            <ConfirmationModal
              open={showDeleteCellModal}
              onClose={() => setShowDeleteCellModal(false)}
              onConfirm={onDeleteCellConfirm}
            >
              <Typography>
                Are you sure you want to delete this cell? This action is
                irreversible and will remove all associated data, including all
                IoT devices associated with this cell.
              </Typography>
              <Typography fontWeight="bold">
                THIS PROCESS IS IRREVERSIBLE!
              </Typography>
            </ConfirmationModal>
          )}
        </Box>
        {selectedDevice && (
          <Slide
            direction="left"
            in={showIOTDevicePanel.open}
            onExited={() =>
              setShowIOTDevicePanel({
                open: false,
                selectedIOTDeviceId: "",
              })
            }
            mountOnEnter
            unmountOnExit
          >
            <Box
              sx={{
                zIndex: 2,
                px: 2,
                backgroundColor: (theme) => theme.palette.background.default,
              }}
            >
              <IOTDeviceDetailPanel
                iotDevice={selectedDevice}
                machineSensors={selectedMachineSensors}
                pulseMeters={selectedPulseMeters}
                cell={cell}
                onClose={() =>
                  setShowIOTDevicePanel((old) => ({
                    open: false,
                    selectedIOTDeviceId: old.selectedIOTDeviceId,
                  }))
                }
              />
            </Box>
          </Slide>
        )}
      </Box>
    </>
  );
}
